import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Hero.css";
import img from "../../Images/img2.jpg";
import img2 from "../../Images/img3.jpg";
import img3 from "../../Images/ourwork.jpg";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBolt,
  FaChartLine,
  FaRobot,
  FaShieldAlt,
  FaTruck,
  FaIndustry,
  FaBoxes,
  FaUserTie,
  FaTimesCircle,
  FaCheckCircle,
} from "react-icons/fa";
import { automationDetails } from "../../utils/automations";
import { Link } from "react-router-dom";
import SerivceCard from "../SerivceCard";
import { reviews } from "../../utils/hero";
import tms from "../../Images/tms.jpg";
import ewaybill from "../../Images/ewaybill.jpg";

const useFadeInOnScroll = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
};

const Hero = () => {
  const slides = [
    {
      id: 1,
      title: "Elevate your business with",
      text: "Simpler - Smarter - Faster",
      image: img,
      alt: "Business automation banner showcasing digital transformation",
    },
    {
      id: 2,
      title: "Automation",
      text: "Great results with AI",
      image: img2,
      alt: "AI automation and workflow optimization illustration",
    },
    {
      id: 3,
      title: "Collaboration",
      text: "Work together",
      image: img3,
      alt: "Team collaboration and productivity improvement",
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [fade, setFade] = useState(false);

  const changeSlide = (direction) => {
    setFade(true);
    setTimeout(() => {
      setCurrentSlide((prev) =>
        direction === "next"
          ? (prev + 1) % slides.length
          : (prev - 1 + slides.length) % slides.length,
      );
      setFade(false);
    }, 500);
  };

  const texts = useMemo(
    () => [
      "Digital Presence",
      "Custom Software",
      "Smart Automation",
      "AI Integration",
      "Web Development",
      "Brand Identity",
      "Smart Workflows",
      "Business Growth",
    ],
    [],
  );

  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [texts.length]);

  const currentText = texts[currentTextIndex];

  const cards = useRef(null);

  const [heroRef, heroVisible] = useFadeInOnScroll();
  const [valueRef, valueVisible] = useFadeInOnScroll();
  const [audienceRef, audienceVisible] = useFadeInOnScroll();
  const [problemRef, problemVisible] = useFadeInOnScroll();
  const [servicesRef, servicesVisible] = useFadeInOnScroll();
  const [productsRef, productsVisible] = useFadeInOnScroll();
  const [howRef, howVisible] = useFadeInOnScroll();
  const [reviewsRef, reviewsVisible] = useFadeInOnScroll();
  const [ctaRef, ctaVisible] = useFadeInOnScroll();

  useEffect(() => {
    const interval = setInterval(() => {
      if (cards.current) {
        let width = cards.current.clientWidth;
        cards.current.scrollLeft += 400;
        if (
          Math.floor(cards.current.scrollLeft) >=
          cards.current.scrollWidth - width
        ) {
          cards.current.scrollLeft = 0;
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const autoSlide = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setFade(false);
      }, 500);
    }, 5000);

    return () => clearInterval(autoSlide);
  }, [slides.length]);

  return (
    <div>
      {/* SEO Hidden Images */}
      <div className="seo-images">
        {slides.map((s) => (
          <img
            key={s.id}
            src={s.image}
            alt={s.alt}
            loading="lazy"
            className="seo-hidden-img"
          />
        ))}
      </div>

      {/* ---------------- HERO SECTION ---------------- */}
      <div className="hero-container">
        <div
          className={`hero-bg bg1 ${fade ? "fade-out" : "fade-in"}`}
          style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
        />
        <div
          className={`hero-bg bg2 ${fade ? "fade-in" : "fade-out"}`}
          style={{
            backgroundImage: `url(${
              slides[(currentSlide + 1) % slides.length].image
            })`,
          }}
        />

        <div className="hero-overlay">
          <div className="hero-content">
            <button
              className="arrow left-arrow"
              onClick={() => changeSlide("prev")}
            >
              <FaArrowLeft />
            </button>

            <div
              ref={heroRef}
              className={`hero-text service-fade ${
                heroVisible ? "visible" : ""
              } ${fade ? "animate-content" : ""}`}
            >
              <h1>{slides[currentSlide].title}</h1>
              <h2 className="highlight">{currentText}</h2>
              <p>{slides[currentSlide].text}</p>

              <Link to="/contactus" className="hero-button">
                Let’s Shake Hands →
              </Link>
            </div>

            <button
              className="arrow right-arrow"
              onClick={() => changeSlide("next")}
            >
              <FaArrowRight />
            </button>
          </div>
        </div>
      </div>

      {/* ---------------- VALUE PROPOSITION ---------------- */}
      <div
        ref={valueRef}
        className={`value-strip service-fade ${valueVisible ? "visible" : ""}`}
      >
        <div className="value-item">
          <FaBolt className="value-icon" />
          <h3>Faster Operations</h3>
          <p>Automate repetitive tasks and reduce manual effort.</p>
        </div>

        <div className="value-item">
          <FaChartLine className="value-icon" />
          <h3>Real-Time Visibility</h3>
          <p>Track trips, invoices, and performance instantly.</p>
        </div>

        <div className="value-item">
          <FaRobot className="value-icon" />
          <h3>Smart Automation</h3>
          <p>AI-powered workflows built for scale.</p>
        </div>

        <div className="value-item">
          <FaShieldAlt className="value-icon" />
          <h3>GST & E-Way Ready</h3>
          <p>Compliance without manual stress.</p>
        </div>
      </div>

      {/* ---------------- WHO IS THIS FOR ---------------- */}
      <div
        ref={audienceRef}
        className={`audience-section service-fade ${
          audienceVisible ? "visible" : ""
        }`}
      >
        <h2 className="section-title">
          Built for <span className="highlight">Growing Businesses</span>
        </h2>
        <p className="section-subtitle">
          Designed for teams managing operations, compliance, and scale.
        </p>
        <div className="audience-cards">
          <div className="audience-card">
            <FaTruck className="icon" /> Transport & Logistics Companies
          </div>
          <div className="audience-card">
            <FaIndustry className="icon" /> Manufacturers & Distributors
          </div>
          <div className="audience-card">
            <FaBoxes className="icon" /> SMEs Handling High Volumes
          </div>
          <div className="audience-card">
            <FaUserTie className="icon" /> Operations & Accounts Teams
          </div>
        </div>
      </div>

      {/* ---------------- PROBLEM → SOLUTION ---------------- */}
      <div
        ref={problemRef}
        className={`problem-solution-section service-fade ${
          problemVisible ? "visible" : ""
        }`}
      >
        <h2 className="section-title">
          Struggling With <span className="highlight">Daily Operations?</span>
        </h2>

        <div className="problem-solution-grid">
          <div className="problem-box">
            <h3>
              <FaTimesCircle className="icon danger" /> Common Problems
            </h3>
            <ul>
              <li>Manual data entry and heavy Excel dependency</li>
              <li>Frequent billing delays and calculation errors</li>
              <li>Lack of real-time tracking and visibility</li>
              <li>Stressful GST and E-Way Bill compliance</li>
              <li>Disconnected teams and constant follow-ups</li>
            </ul>
          </div>

          <div className="solution-box">
            <h3>
              <FaCheckCircle className="icon success" /> Our Solution
            </h3>
            <ul>
              <li>A single, centralized automation system</li>
              <li>Faster, accurate billing with minimal manual effort</li>
              <li>Live dashboards for trips, invoices, and performance</li>
              <li>Automated GST & E-Way Bills with compliance checks</li>
              <li>Smooth collaboration between operations and accounts</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ---------------- SERVICES ---------------- */}
      <div
        ref={servicesRef}
        className={`services-section service-fade ${
          servicesVisible ? "visible" : ""
        }`}
      >
        <h2 className="services-title">
          <span className="services-gray">Our</span>{" "}
          <span className="services-orange">Services</span>
        </h2>

        <p className="services-subtitle">
          Not sure where to start? Check out how we can help your brand grow.
        </p>

        <div className="services-cards">
          {automationDetails.map((automationDetail) => (
            <SerivceCard detail={automationDetail} key={automationDetail.id} />
          ))}
        </div>
      </div>

      {/* ---------------- PRODUCTS ---------------- */}
      <div
        ref={productsRef}
        className={`products-section service-fade ${
          productsVisible ? "visible" : ""
        }`}
      >
        <h2 className="products-title">
          <span className="products-gray">Our Key</span>{" "}
          <span className="products-orange">Products</span>
        </h2>

        <p className="products-subtitle">
          Explore our powerful business automation tools designed to simplify
          operations.
        </p>

        <div className="products-cards">
          <div className="hero-product-card">
            <img src={tms} alt="TMS" className="home-product-image" />
            <h3>Transport Management System (TMS)</h3>
            <p>
              Organize consignors, trips, freight, and invoices in one place.
            </p>
            <Link to="/saraltransport" className="btn-container">
              <span className="hero-btn">Explore TMS</span>
              <span className="btn-icon">→</span>
              <span className="btn-bg"></span>
            </Link>
          </div>

          <div className="hero-product-card">
            <img
              src={ewaybill}
              alt="E-Way Bill"
              className="home-product-image"
            />
            <h3>E-Way Bill Management</h3>
            <p>
              Generate, manage, extend, and automate E-Way Bills with real-time
              updates.
            </p>
            <Link to="/saraleway" className="btn-container">
              <span className="hero-btn">Explore E-Way Bill</span>
              <span className="btn-icon">→</span>
              <span className="btn-bg"></span>
            </Link>
          </div>
        </div>
      </div>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <div
        ref={howRef}
        className={`how-it-works-section service-fade ${
          howVisible ? "visible" : ""
        }`}
      >
        <h2 className="section-title">
          How We <span className="highlight">Work With You</span>
        </h2>

        <div className="steps">
          <div className="step-card">
            <span>1</span>
            <h4>Understand Workflow</h4>
            <p>We study your operations and challenges.</p>
          </div>
          <div className="step-card">
            <span>2</span>
            <h4>Customize Automation</h4>
            <p>Tailored features based on your needs.</p>
          </div>
          <div className="step-card">
            <span>3</span>
            <h4>Integrate & Automate</h4>
            <p>AI workflows, reports & compliance.</p>
          </div>
          <div className="step-card">
            <span>4</span>
            <h4>Launch & Scale</h4>
            <p>Grow with real-time insights.</p>
          </div>
        </div>
      </div>

      {/* ---------------- REVIEWS ---------------- */}
      <div
        ref={reviewsRef}
        className={`happy-clients-section service-fade ${
          reviewsVisible ? "visible" : ""
        }`}
      >
        <h2 className="happy-clients-title">
          <span className="happy-clients-gray">Hear from our</span>{" "}
          <span className="happy-clients-orange">Happy Clients</span>
        </h2>

        <p className="happy-clients-subtitle">
          Learn how our solutions helped our clients’ businesses
        </p>

        <div className="testimonial-section-home">
          <div className="testimonial-cards" ref={cards}>
            {reviews.map((review, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-quote-icon">“</div>
                <p className="testimonial-quote-home">{review.review}</p>
                <div className="testimonial-author">{review.clientName}</div>
                <div className="testimonial-title">{review.companyName}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------------- SOFT CTA ---------------- */}
      <div
        ref={ctaRef}
        className={`soft-cta-section service-fade ${
          ctaVisible ? "visible" : ""
        }`}
      >
        <h2>
          Ready to <span className="highlight">Simplify Your Business?</span>
        </h2>
        <p>
          Let’s automate your operations and remove daily bottlenecks — without
          disrupting your workflow.
        </p>
        <div className="cta-buttons">
          <Link to="/contactus" className="primary-btn">
            Talk to an Expert →
          </Link>
          <Link to="/services" className="secondary-btn">
            Explore Solutions
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Hero;
